class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isComposing = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.input.addEventListener('compositionstart', () => {
      this.isComposing = true;
    });
    this.input.addEventListener('compositionend', () => {
      this.isComposing = false;
      this.onChange();
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.showDefaultState();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
  const currentSearchTerm = this.getQuery();

  if (!currentSearchTerm.length) {
    this.showDefaultState();
    return;
  }

  // Always check if we need to get new results or just reopen existing ones
  if (this.searchTerm !== currentSearchTerm) {
    // Search term was changed from other search input, treat it as a user change
    this.onChange();
  } else if (this.getAttribute('results') === 'true') {
    this.open();
  } else {
    this.getSearchResults(this.searchTerm);
  }
}

  showDefaultState() {
  // Get the pre-rendered Liquid default content from mobile search
  const mobileDefaultContent = document.querySelector('.mobile-search-default-content');
  
  if (!mobileDefaultContent) {
    // Fallback if mobile content doesn't exist
    this.setAttribute('results', true);
    this.open();
    return;
  }
  
  // Extract trending section
  const trendingSection = mobileDefaultContent.querySelector('.mobile-search-section:first-child');
  let trendingHTML = '';
  
  if (trendingSection) {
    const trendingTitle = trendingSection.querySelector('.mobile-search-section-title').textContent;
    const trendingItems = Array.from(trendingSection.querySelectorAll('.trending-item'));
    
    if (trendingItems.length > 0) {
      trendingHTML = `
        <div class="predictive-search__trending">
          <h2 class="predictive-search__heading text-body caption-with-letter-spacing">
            ${trendingTitle}
          </h2>
          <div class="predictive-search__trending-items">
            ${trendingItems.slice(0, 6).map(item => 
              `<a href="${item.href}" class="predictive-search__trending-item">${item.textContent.trim()}</a>`
            ).join('')}
          </div>
        </div>
      `;
    }
  }
  
  // Extract browse section
  const browseSection = mobileDefaultContent.querySelector('.mobile-search-section:last-child');
  let browseHTML = '';
  
  if (browseSection) {
    const browseTitle = browseSection.querySelector('.mobile-search-section-title').textContent;
    const browseItems = Array.from(browseSection.querySelectorAll('.browse-item'));
    
    if (browseItems.length > 0) {
      browseHTML = `
        <div class="predictive-search__browse">
          <h2 class="predictive-search__heading text-body caption-with-letter-spacing">
            ${browseTitle}
          </h2>
          <div class="predictive-search__browse-grid">
            ${browseItems.slice(0, 8).map(item => 
              `<a href="${item.href}" class="predictive-search__browse-item">
                <span class="predictive-search__browse-text">${item.querySelector('.browse-item-text').textContent.trim()}</span>
              </a>`
            ).join('')}
          </div>
        </div>
      `;
    }
  }
  
  const defaultHTML = `
    <div role="listbox">
      <div class="predictive-search__default-wrapper">
        ${trendingHTML}
        ${browseHTML}
      </div>
    </div>
  `;
  
  this.predictiveSearchResults.innerHTML = defaultHTML;
  this.setAttribute('results', true);
  this.open();
}

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (this.isComposing || event.isComposing || event.keyCode === 229) return;

    if (!this.getQuery().length) {
      this.showDefaultState();
      return;
    }
    
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const localePrefix = window.location.pathname.split('/').filter(Boolean)[0];
    const isNonDefault = localePrefix && localePrefix.length <= 5 && localePrefix !== 'search' && localePrefix !== 'products' && localePrefix !== 'collections' && localePrefix !== 'pages';
    const queryKey = `${isNonDefault ? localePrefix : 'default'}-${searchTerm.replace(' ', '-').toLowerCase()}`;

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (response.status === 417 && isNonDefault) {
          this.showFallbackSkeleton();
          return this.getSearchResultsFallback(searchTerm, queryKey, localePrefix);
        }
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        if (!text) return;
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });
  }
  showFallbackSkeleton() {
    const skeleton = `
      <div class="predictive-search__skeleton">
        ${[1,2,3].map(() => `
          <div class="predictive-search__skeleton-item">
            <div class="predictive-search__skeleton-image"></div>
            <div class="predictive-search__skeleton-text">
              <div class="predictive-search__skeleton-line predictive-search__skeleton-line--short"></div>
              <div class="predictive-search__skeleton-line predictive-search__skeleton-line--long"></div>
              <div class="predictive-search__skeleton-line predictive-search__skeleton-line--medium"></div>
            </div>
          </div>
        `).join('')}
      </div>`;
    this.predictiveSearchResults.innerHTML = skeleton;
    this.setAttribute('results', true);
    this.open();
  }

  getLocalizedString(key, terms = '') {
    const strings = {
      products: { zh: '產品', ar: 'المنتجات', es: 'Productos', de: 'Produkte', fr: 'Produits', ja: '商品' },
      search_for: { zh: '搜尋「TERMS」', ar: '"TERMS" البحث عن', es: 'Buscar "TERMS"', de: 'Suche nach "TERMS"', fr: 'Rechercher "TERMS"', ja: '「TERMS」を検索' },
    };
    const locale = (window.Shopify?.locale || document.documentElement.lang || 'en').substring(0, 2).toLowerCase();
    const str = strings[key]?.[locale] || (key === 'products' ? 'Products' : `Search For "${terms}"`);
    return str.replace('TERMS', terms);
  }

  getSearchResultsFallback(searchTerm, queryKey, localePrefix) {
    return fetch(`/${localePrefix}/search?q=${encodeURIComponent(searchTerm)}&type=product`, {
      signal: this.abortController.signal,
    })
      .then((r) => {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const items = Array.from(doc.querySelectorAll('#product-grid .grid__item')).slice(0, 6);

        const productItems = items.map((item, i) => {
          const titleEl = item.querySelector('.card__heading a');
          const title = titleEl?.textContent.trim() || '';
          const href = titleEl?.getAttribute('href') || '';
          const img = item.querySelector('.card__media img, .card img');
          const imgSrc = img?.getAttribute('src') || '';
          const imgAlt = img?.getAttribute('alt') || title;
          const currentPrice = item.querySelector('.custom-price-current')?.textContent.trim() || '';
          const originalPrice = item.querySelector('.custom-price-original')?.textContent.trim() || '';
          const badgeText = item.querySelector('.custom-price-badge')?.textContent.trim() || '';
          const vendor = item.querySelector('.card__vendor, [class*="vendor"]')?.textContent.trim() || '';

          const priceHTML = currentPrice
            ? `<div class="price n-price price--on-sale">
                <div class="price__container pp-cont">
                  <div class="price__sale">
                    <div class="price-sale-container" style="display: flex; align-items: center; gap: 10px; flex-wrap: wrap;">
                      <span class="price-item price-item--sale price-item--main" style="font-weight: bold; font-size: 0.8em; color: #000;">${currentPrice}</span>
                      ${originalPrice ? `<span class="price-item price-item--compare" style="text-decoration: line-through; color: #666; font-size: 0.6em;">${originalPrice}</span>` : ''}
                      ${badgeText ? `<span class="discount-percentage" style="background: linear-gradient(135deg, #fac463 0%, #f4a340 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; font-weight: bold; font-size: 0.6em;">${badgeText}</span>` : ''}
                    </div>
                  </div>
                </div>
              </div>`
            : '';

          return `<li id="predictive-search-option-product-${i + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
                    <a href="${href}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">${imgSrc ? `<img class="predictive-search__image" src="${imgSrc}" alt="${imgAlt}" width="50" height="50">` : ''}<div class="predictive-search__item-content predictive-search__item-content--centered">
                        <span class="visually-hidden">Vendor:</span>
                        <span class="predictive-search__item-vendor">${vendor}</span><p class="predictive-search__item-heading h5">${title}</p>
                        ${priceHTML}
                      </div>
                    </a>
                  </li>`;
        }).join('');

        const searchForText = document.querySelector('[data-predictive-search-search-for-text]')?.closest('.predictive-search__search-for-button') ? 'Search For' : 'Search For';

        const resultsMarkup = `
          <div id="predictive-search-results">
            <div id="predictive-search-results-groups-wrapper">
              <div class="predictive-search__result-group">
                <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">${this.getLocalizedString('products')}</h2>
                <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="listbox" aria-labelledby="predictive-search-products">
                  ${productItems}
                </ul>
              </div>
            </div>
            <div class="predictive-search__search-for-button">
              <button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" data-predictive-search-search-for-text>
                ${this.getLocalizedString('search_for', searchTerm)} →
              </button>
            </div>
            <span class="hidden" data-predictive-search-live-region-count-value>${items.length} products</span>
          </div>`;

        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);